/**
 * Color Customizer Web Component
 *
 * Handles the interactive color customization wizard for products.
 * Supports two modes:
 * - Signature Colorations: Pre-designed color options
 * - Build Your Own: Step-by-step customizer with pour styles, base colors, and add-ins
 */

import { animate } from "vendor";

class ColorCustomizer extends HTMLElement {
  constructor() {
    super();

    // State management
    this.mode = this.dataset.defaultMode || "signature";
    this.currentStep = 0;
    this.totalSteps = 1;

    // Selection state
    this.selections = {
      signature: null,
      pourStyle: null,
      colorSlots: [], // Array of { baseColor, baseColorName, baseColorHex, addIn, addInName, addInHex, slotLabel }
    };

    // Price calculation
    this.priceModifiers = {
      pourStyle: 0,
      baseColors: 0,
      addIns: 0,
    };

    // Cache DOM elements
    this.formId = this.getAttribute("form-id");
    this.sectionId = this.getAttribute("section-id");
    this.form = document.getElementById(this.formId);

    // Color complexities data (for variant selection)
    this.colorComplexities = [];

    // Cache for fetched pour-style SVGs (keyed by URL)
    this.pourSvgCache = new Map();
  }

  connectedCallback() {
    this.cacheElements();
    this.parseColorComplexities();
    this.bindEvents();
    this.initializeState();
    this.loadCustomSvgMask();
  }

  cacheElements() {
    // Mode toggle
    this.modeToggles = this.querySelectorAll("[data-mode-toggle]");
    this.modeContainers = this.querySelectorAll("[data-mode]");

    // Signature mode elements
    this.signatureSwatches = this.querySelectorAll("[data-signature-swatch]");
    this.signatureHelp = this.querySelector("[data-signature-help]");
    this.signaturePreview = this.querySelector("[data-signature-preview]");
    this.signaturePreviewImage = this.querySelector(
      "[data-signature-preview-image]",
    );

    // Customizer mode elements
    this.pourOptions = this.querySelectorAll("[data-pour-option]");
    this.pourNextButton = this.querySelector("[data-pour-next-button]");
    this.colorStepsContainer = this.querySelector(
      "[data-color-steps-container]",
    );
    this.colorStepTemplate = this.querySelector("[data-color-step-template]");
    this.progressIndicator = this.querySelector("[data-progress-indicator]");
    this.progressFill = this.querySelector("[data-progress-fill]");
    this.progressText = this.querySelector("[data-progress-text]");

    // Preview elements
    this.previewContainer = this.querySelector("[data-preview-container]");
    this.previewSvg = this.querySelector("[data-preview-svg]");

    // SVG child elements - search within previewSvg for reliability with SVG namespace
    if (this.previewSvg) {
      this.pourLayer = this.previewSvg.querySelector("[data-pour-layer]");
      this.pourDefs = this.previewSvg.querySelector("[data-pour-defs]");
      this.addinDefs = this.previewSvg.querySelector("[data-addin-defs]");
      this.addInLayer = this.previewSvg.querySelector("[data-add-in-layer]");
      this.pourFallback = this.previewSvg.querySelector("[data-pour-fallback]");
      this.addinFallback = this.previewSvg.querySelector(
        "[data-addin-fallback]",
      );
    } else {
      console.warn("[ColorCustomizer] Preview SVG element not found");
    }

    // Container for dynamic line item property inputs
    this.lineItemContainer = this.querySelector("[data-line-item-inputs]");
  }

  parseColorComplexities() {
    const dataElement = this.querySelector("[data-color-complexities]");
    if (dataElement) {
      try {
        this.colorComplexities = JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error("Failed to parse color complexities:", e);
      }
    }
  }

  bindEvents() {
    // Mode toggle
    this.modeToggles.forEach((toggle) => {
      toggle.addEventListener("change", (e) =>
        this.handleModeChange(e.target.value),
      );
    });

    // Signature swatch selection - listen on input change to avoid mobile double-tap
    this.signatureSwatches.forEach((swatch) => {
      const input = swatch.querySelector("[data-signature-input]");
      if (input) {
        input.addEventListener("change", () =>
          this.handleSignatureSelect(swatch),
        );
      }
      // Also handle direct clicks on label for accessibility
      swatch.addEventListener("click", (e) => {
        // Only handle if not clicking the input itself (input handles its own change)
        if (e.target.closest("[data-signature-input]")) return;
        // Manually check the input and trigger change
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    });

    // Pour style selection
    this.pourOptions.forEach((option) => {
      option.addEventListener("click", () =>
        this.handlePourStyleSelect(option),
      );
    });

    // Pour style Next button
    if (this.pourNextButton) {
      this.pourNextButton.addEventListener("click", () => {
        if (this.selections.pourStyle) {
          this.navigateToStep(1);
        }
      });
    }
  }

  initializeState() {
    // Set initial mode visibility (without animation)
    this.setInitialPreviewVisibility();
    this.updateModeVisibility(false); // false = skip animation on init

    // If signature mode and we have colorations, auto-select the first one
    // (metafield array order determines DOM order)
    if (this.mode === "signature" && this.signatureSwatches.length > 0) {
      const firstSwatch = this.signatureSwatches[0];
      if (firstSwatch) {
        // Auto-select first coloration
        this.handleSignatureSelect(firstSwatch);
      }
    }

    // If customizer mode and we have pour styles, auto-select the first one
    if (this.mode === "customizer" && this.pourOptions.length > 0) {
      const firstPourOption = this.pourOptions[0];
      if (firstPourOption) {
        // Auto-select first pour style
        this.handlePourStyleSelect(firstPourOption);
      }
    }
  }

  /**
   * Set initial preview visibility without animation (called on page load)
   */
  setInitialPreviewVisibility() {
    const showSignature = this.mode === "signature";

    if (this.previewSvg) {
      if (showSignature) {
        this.previewSvg.classList.add("is-hidden");
        this.previewSvg.style.opacity = "0";
      } else {
        this.previewSvg.classList.remove("is-hidden");
        this.previewSvg.style.opacity = "1";
      }
    }

    if (this.signaturePreview) {
      // Always remove the hidden attribute (set in HTML for initial load)
      this.signaturePreview.removeAttribute("hidden");

      if (showSignature) {
        this.signaturePreview.classList.remove("is-hidden");
        this.signaturePreview.style.opacity = "1";
      } else {
        this.signaturePreview.classList.add("is-hidden");
        this.signaturePreview.style.opacity = "0";
      }
    }
  }

  handleModeChange(newMode) {
    const previousMode = this.mode;
    this.mode = newMode;
    this.updateModeVisibility();

    // Only reset the mode we're leaving, not the one we're switching to
    // This preserves state when switching back
    if (previousMode === "customizer" && newMode === "signature") {
      // Switching TO signature - restore signature state if we had one
      if (this.selections.signature) {
        // Re-apply the saved signature selection UI
        const savedHandle = this.selections.signature.handle;
        this.signatureSwatches.forEach((s) => {
          const isSelected = s.dataset.colorationHandle === savedHandle;
          s.classList.toggle("is-selected", isSelected);
          const input = s.querySelector("[data-signature-input]");
          if (input) input.checked = isSelected;
        });
        this.updateSignatureHelpText();
        this.updateSignaturePreview(
          this.querySelector(
            `[data-signature-swatch][data-coloration-handle="${savedHandle}"]`,
          ),
          true, // Skip animation - mode crossfade handles the transition
        );
      } else if (this.signatureSwatches.length > 0) {
        // No previous selection, auto-select first
        this.handleSignatureSelect(this.signatureSwatches[0]);
      }
    } else if (previousMode === "signature" && newMode === "customizer") {
      // Switching TO customizer - restore customizer state if we had one
      if (this.selections.pourStyle) {
        // Re-apply the saved pour style selection UI
        const savedHandle = this.selections.pourStyle.handle;
        this.pourOptions.forEach((opt) => {
          const isSelected = opt.dataset.pourHandle === savedHandle;
          opt.classList.toggle("is-selected", isSelected);
          const input = opt.querySelector("[data-pour-input]");
          if (input) input.checked = isSelected;
        });

        // Restore pour style preview
        this.updatePreviewPourStyle(savedHandle);

        // Rebuild color steps if we have slot data
        if (this.selections.colorSlots.length > 0) {
          this.rebuildColorSteps();
        }

        // Show progress indicator
        this.updateProgress();
      } else if (this.pourOptions.length > 0) {
        // No previous selection, auto-select first pour style
        this.handlePourStyleSelect(this.pourOptions[0]);
      }
    }

    this.updateLineItemProperty();
    this.updatePreview();

    // Defer variant selection to avoid triggering section re-render during animation
    // This also prevents flickering from rapid DOM changes
    this.deferredVariantUpdate();
  }

  /**
   * Debounced variant update to prevent section re-renders during mode transitions
   */
  deferredVariantUpdate() {
    if (this._variantUpdateTimer) {
      clearTimeout(this._variantUpdateTimer);
    }
    this._variantUpdateTimer = setTimeout(() => {
      this.updateVariantSelection();
    }, 300); // Wait for animations to complete
  }

  updateModeVisibility(animate = true) {
    this.modeContainers.forEach((container) => {
      const containerMode = container.dataset.mode;
      container.hidden = containerMode !== this.mode;
    });

    // Update toggle button styles
    this.modeToggles.forEach((toggle) => {
      const button = toggle.nextElementSibling;
      if (button) {
        if (toggle.checked) {
          button.classList.remove("button--outline");
        } else {
          button.classList.add("button--outline");
        }
      }
    });

    // Animate preview crossfade between modes (skip on initial load)
    if (animate) {
      this.animatePreviewTransition();
    }
  }

  /**
   * Animate crossfade transition between signature and customizer previews
   */
  animatePreviewTransition() {
    const showSignature = this.mode === "signature";

    // Determine which elements to fade in/out
    const fadeOut = showSignature ? this.previewSvg : this.signaturePreview;
    const fadeIn = showSignature ? this.signaturePreview : this.previewSvg;

    if (!fadeIn && !fadeOut) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      // Instant switch without animation
      if (fadeOut) {
        fadeOut.style.opacity = "0";
        fadeOut.classList.add("is-hidden");
      }
      if (fadeIn) {
        fadeIn.style.opacity = "1";
        fadeIn.classList.remove("is-hidden");
      }
      return;
    }

    // Prepare fade-in element: set opacity to 0 BEFORE removing is-hidden
    // to prevent flash of content
    if (fadeIn) {
      fadeIn.style.opacity = "0";
      // Force style recalculation before removing hidden class
      fadeIn.offsetHeight;
      fadeIn.classList.remove("is-hidden");
    }

    // Animate crossfade
    const duration = 0.2;
    const easing = [0.25, 0.46, 0.45, 0.94]; // ease-out-quad

    // Fade out current preview
    if (fadeOut) {
      animate(fadeOut, { opacity: [1, 0] }, { duration, easing }).finished.then(
        () => {
          fadeOut.classList.add("is-hidden");
        },
      );
    }

    // Fade in new preview (starts halfway through fade-out for smooth crossfade)
    if (fadeIn) {
      animate(
        fadeIn,
        { opacity: [0, 1] },
        { duration, delay: duration * 0.5, easing },
      );
    }
  }

  resetSelections() {
    if (this.mode === "signature") {
      // Reset customizer mode state
      this.selections.pourStyle = null;
      this.selections.colorSlots = [];
      this.clearColorSteps();
      this.currentStep = 0;
      this.totalSteps = 1;

      // Reset customizer UI state
      this.pourOptions.forEach((opt) => {
        opt.classList.remove("is-selected");
        const input = opt.querySelector("[data-pour-input]");
        if (input) input.checked = false;
      });

      // Hide progress indicator
      if (this.progressIndicator) {
        this.progressIndicator.hidden = true;
      }

      // Reset preview to default state
      if (this.pourLayer) {
        this.pourLayer.setAttribute(
          "fill",
          `url(#pour-solid-${this.sectionId})`,
        );
        this.pourLayer.dataset.currentPour = "solid";
      }
    } else {
      // Reset signature mode state
      this.selections.signature = null;

      // Reset signature UI state
      this.signatureSwatches.forEach((s) => {
        s.classList.remove("is-selected");
        const input = s.querySelector("[data-signature-input]");
        if (input) input.checked = false;
      });

      // Hide signature help text
      if (this.signatureHelp) {
        this.signatureHelp.hidden = true;
      }

      // Hide signature preview (using class for animation compatibility)
      if (this.signaturePreview) {
        this.signaturePreview.classList.add("is-hidden");
        this.signaturePreview.style.opacity = "0";
      }
    }
    this.priceModifiers = { pourStyle: 0, baseColors: 0, addIns: 0 };
  }

  // ==================== SIGNATURE COLORATIONS ====================

  handleSignatureSelect(swatch) {
    // Update selection state
    const handle = swatch.dataset.colorationHandle;
    const name = swatch.dataset.colorationName;
    const complexityHandle = swatch.dataset.complexityHandle;
    const complexityOptionName = swatch.dataset.complexityOptionName;
    const priceModifier = parseInt(swatch.dataset.priceModifier) || 0;
    const regularPrice = parseInt(swatch.dataset.regularPrice) || 0;
    const isOnSale = swatch.dataset.isOnSale === "true";
    const infoUrl = swatch.dataset.infoUrl;

    this.selections.signature = {
      handle,
      name,
      complexityHandle,
      complexityOptionName,
      priceModifier,
      regularPrice,
      isOnSale,
      infoUrl,
    };

    // Update UI
    this.signatureSwatches.forEach((s) => {
      s.classList.toggle("is-selected", s === swatch);
    });

    // Update help text
    this.updateSignatureHelpText();

    // Update preview
    this.updateSignaturePreview(swatch);

    // Update line item property
    this.updateLineItemProperty();

    // Update variant selection
    this.updateVariantSelection();
  }

  updateSignatureHelpText() {
    if (!this.signatureHelp || !this.selections.signature) return;

    const { name, priceModifier, infoUrl } = this.selections.signature;

    this.signatureHelp.hidden = false;

    const nameSpan = this.signatureHelp.querySelector("[data-selection-name]");
    const priceSpan = this.signatureHelp.querySelector("[data-price-modifier]");
    const infoLink = this.signatureHelp.querySelector("[data-info-link]");

    if (nameSpan) nameSpan.textContent = name;

    if (priceSpan) {
      const regularPrice = parseFloat(this.selections.signature.regularPrice || 0);
      if (priceModifier > 0) {
        priceSpan.textContent = ` (+${this.formatMoney(priceModifier * 100)})`;
      } else if (regularPrice > 0) {
        priceSpan.innerHTML = ` (+<s>${this.formatMoney(regularPrice * 100)}</s>)`;
      } else {
        priceSpan.textContent = "";
      }
    }

    if (infoLink) {
      if (infoUrl) {
        infoLink.href = infoUrl;
        infoLink.hidden = false;
      } else {
        infoLink.hidden = true;
      }
    }
  }

  updateSignaturePreview(swatch, skipAnimation = false) {
    if (!this.signaturePreviewImage) return;

    // Prefer the dedicated preview_image from metaobject
    const previewImageUrl = swatch.dataset.previewImage;
    let newSrc;

    if (previewImageUrl) {
      // Use the preview_image from the metaobject
      newSrc = previewImageUrl;
    } else {
      // Fallback: use the swatch image at larger size
      const img = swatch.querySelector("img");
      if (img) {
        const srcset = img.srcset || img.src;
        newSrc = srcset.replace(/width=\d+/, "width=400") || img.src;
      }
    }

    if (!newSrc) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Skip animation if requested (e.g., during mode switch where crossfade handles transition)
    const shouldAnimate = !skipAnimation && !prefersReducedMotion;

    // Preload the new image first
    const preloadImg = new Image();
    preloadImg.src = newSrc;

    const applyImage = () => {
      if (!shouldAnimate) {
        // Instant switch without animation
        this.signaturePreviewImage.src = newSrc;
        this.signaturePreviewImage.alt = this.selections.signature.name;
      } else {
        // Crossfade: fade out, swap, fade in
        const duration = 0.3;
        const easing = [0.42, 0, 0.58, 1]; // ease-in-out

        animate(
          this.signaturePreviewImage,
          { opacity: [1, 0] },
          { duration, easing },
        ).finished.then(() => {
          this.signaturePreviewImage.src = newSrc;
          this.signaturePreviewImage.alt = this.selections.signature.name;
          animate(
            this.signaturePreviewImage,
            { opacity: [0, 1] },
            { duration, easing },
          );
        });
      }
    };

    // Wait for preload to complete, or apply immediately if already cached
    if (preloadImg.complete) {
      applyImage();
    } else {
      preloadImg.onload = applyImage;
      // Fallback: if image fails to load, still try to display it
      preloadImg.onerror = applyImage;
    }

    if (this.signaturePreview) {
      this.signaturePreview.classList.remove("is-hidden");
      this.signaturePreview.style.opacity = "1";
    }
  }

  // ==================== BUILD YOUR OWN CUSTOMIZER ====================

  handlePourStyleSelect(option) {
    const handle = option.dataset.pourHandle;
    const name = option.dataset.pourName;
    const colorCount = parseInt(option.dataset.colorCount) || 1;
    const priceModifier = parseInt(option.dataset.priceModifier) || 0;

    let colorLabels = [];
    try {
      colorLabels = JSON.parse(option.dataset.colorLabels || "[]");
    } catch (e) {
      colorLabels = [];
    }

    // Get SVG URL if available
    const svgUrl = option.dataset.pourSvgUrl || null;
    console.log(
      "[ColorCustomizer] Pour style selected:",
      handle,
      "SVG URL from data attr:",
      svgUrl,
    );

    // Update selection
    this.selections.pourStyle = {
      handle,
      name,
      colorCount,
      colorLabels,
      priceModifier,
      svgUrl,
    };

    this.priceModifiers.pourStyle = priceModifier;

    // Update UI
    this.pourOptions.forEach((opt) => {
      opt.classList.toggle("is-selected", opt === option);
    });

    // Generate color selection steps
    this.generateColorSteps(colorCount, colorLabels);

    // Update preview
    this.updatePreviewPourStyle(handle);

    // Show progress indicator
    this.updateProgress();

    // Enable the Next button now that a pour style is selected
    if (this.pourNextButton) {
      this.pourNextButton.disabled = false;
    }
  }

  generateColorSteps(colorCount, colorLabels) {
    if (!this.colorStepsContainer || !this.colorStepTemplate) return;

    // Clear existing steps
    this.clearColorSteps();

    // Reset color selections
    this.selections.colorSlots = [];
    this.priceModifiers.baseColors = 0;
    this.priceModifiers.addIns = 0;

    // Total steps = 1 (pour style) + colorCount
    this.totalSteps = 1 + colorCount;

    // Generate steps for each color slot
    for (let i = 0; i < colorCount; i++) {
      const slotLabel = colorLabels[i] || `Color ${i + 1}`;
      const stepIndex = i + 1; // Pour style is step 0

      // Clone template
      const template = this.colorStepTemplate.content.cloneNode(true);
      const step = template.querySelector(".color-customizer__step");

      // Set data attributes
      step.dataset.stepIndex = stepIndex;
      step.dataset.colorSlot = i;
      step.hidden = true; // Hidden initially

      // Set labels
      const stepLabel = step.querySelector("[data-step-label]");
      const slotLabelEl = step.querySelector("[data-slot-label]");

      if (stepLabel) stepLabel.textContent = `Step ${stepIndex + 1}`;
      if (slotLabelEl) slotLabelEl.textContent = slotLabel;

      // Set input names
      const baseInputs = step.querySelectorAll("[data-base-input]");
      const addInInputs = step.querySelectorAll("[data-add-in-input]");

      baseInputs.forEach((input) => {
        input.name = `${this.id}-base-color-${i}`;
      });

      addInInputs.forEach((input) => {
        input.name = `${this.id}-add-in-${i}`;
      });

      // Initialize selection state
      this.selections.colorSlots[i] = {
        slotLabel,
        baseColor: null,
        baseColorName: null,
        baseColorHex: null,
        addIn: "none",
        addInName: null,
        addInHex: null,
      };

      // Bind events for this step
      this.bindColorStepEvents(step, i);

      // Add to container
      this.colorStepsContainer.appendChild(step);

      // Auto-select first base color and "none" add-in to ensure valid state
      this.autoSelectDefaults(step, i);
    }

    // Update line item property with initial defaults
    this.updateLineItemProperty();
  }

  autoSelectDefaults(step, slotIndex) {
    // Get all base color swatches sorted by order
    const baseSwatches = [...step.querySelectorAll("[data-base-swatch]")].sort(
      (a, b) => {
        const orderA = a.style.order !== "" ? parseInt(a.style.order) : 9999;
        const orderB = b.style.order !== "" ? parseInt(b.style.order) : 9999;
        return orderA - orderB;
      },
    );

    // Auto-select first base color
    if (baseSwatches.length > 0) {
      const firstSwatch = baseSwatches[0];
      this.handleBaseColorSelect(firstSwatch, slotIndex);
    }

    // "None" add-in is already checked in template, just ensure state is set
    const noneSwatch = step.querySelector(
      '[data-add-in-swatch][data-add-in-handle="none"]',
    );
    if (noneSwatch) {
      this.handleAddInSelect(noneSwatch, slotIndex);
    }
  }

  bindColorStepEvents(step, slotIndex) {
    // Back button
    const backButton = step.querySelector("[data-back-button]");
    if (backButton) {
      backButton.addEventListener("click", () =>
        this.navigateToStep(this.currentStep - 1),
      );
    }

    // Next button
    const nextButton = step.querySelector("[data-next-button]");
    if (nextButton) {
      nextButton.addEventListener("click", () => {
        // If on last step (Complete button), scroll to buy buttons
        const isLastStep = this.currentStep === this.totalSteps - 1;
        if (isLastStep) {
          this.scrollToBuyButtons();
          return;
        }
        if (this.validateCurrentStep()) {
          this.navigateToStep(this.currentStep + 1);
        }
      });
    }

    // Base color swatches
    const baseSwatches = step.querySelectorAll("[data-base-swatch]");
    baseSwatches.forEach((swatch) => {
      swatch.addEventListener("click", () =>
        this.handleBaseColorSelect(swatch, slotIndex),
      );
    });

    // Add-in swatches
    const addInSwatches = step.querySelectorAll("[data-add-in-swatch]");
    addInSwatches.forEach((swatch) => {
      swatch.addEventListener("click", () =>
        this.handleAddInSelect(swatch, slotIndex),
      );
    });
  }

  handleBaseColorSelect(swatch, slotIndex) {
    const handle = swatch.dataset.colorHandle;
    const name = swatch.dataset.colorName;
    const hex = swatch.dataset.colorHex;
    const priceModifier = parseInt(swatch.dataset.priceModifier) || 0;

    // Update selection (include price modifier)
    this.selections.colorSlots[slotIndex] = {
      ...this.selections.colorSlots[slotIndex],
      baseColor: handle,
      baseColorName: name,
      baseColorHex: hex,
      baseColorPrice: priceModifier,
    };

    // Update price modifiers
    this.recalculateBaseColorPrice();

    // Update UI
    const step = swatch.closest("[data-step]");
    step.querySelectorAll("[data-base-swatch]").forEach((s) => {
      s.classList.toggle("is-selected", s === swatch);
    });

    this.updateStepSelectionLabels(step, slotIndex);

    // Check the radio
    const input = swatch.querySelector("[data-base-input]");
    if (input) input.checked = true;

    // Update preview
    this.updatePreviewColors();

    // Update line item
    this.updateLineItemProperty();

    // Update variant
    this.updateVariantSelection();
  }

  handleAddInSelect(swatch, slotIndex) {
    const handle = swatch.dataset.addInHandle;
    const name = swatch.dataset.addInName;
    const hex = swatch.dataset.addInHex;
    const priceModifier = parseInt(swatch.dataset.priceModifier) || 0;

    // Update selection (include price modifier)
    this.selections.colorSlots[slotIndex] = {
      ...this.selections.colorSlots[slotIndex],
      addIn: handle,
      addInName: handle === "none" ? null : name,
      addInHex: handle === "none" ? null : hex,
      addInPrice: handle === "none" ? 0 : priceModifier,
    };

    // Update price modifiers
    this.recalculateAddInPrice();

    // Update UI
    const step = swatch.closest("[data-step]");
    step.querySelectorAll("[data-add-in-swatch]").forEach((s) => {
      s.classList.toggle("is-selected", s === swatch);
    });

    this.updateStepSelectionLabels(step, slotIndex);

    // Check the radio
    const input = swatch.querySelector("[data-add-in-input]");
    if (input) input.checked = true;

    // Update preview
    this.updatePreviewColors();

    // Update line item
    this.updateLineItemProperty();

    // Update variant
    this.updateVariantSelection();
  }

  recalculateBaseColorPrice() {
    // Sum up all base color price modifiers from selections
    this.priceModifiers.baseColors = this.selections.colorSlots.reduce(
      (sum, slot) => sum + (slot.baseColorPrice || 0),
      0,
    );
  }

  recalculateAddInPrice() {
    // Sum up all add-in price modifiers from selections
    this.priceModifiers.addIns = this.selections.colorSlots.reduce(
      (sum, slot) => sum + (slot.addInPrice || 0),
      0,
    );
  }

  updateStepSelectionLabels(step, slotIndex) {
    const slot = this.selections.colorSlots[slotIndex];
    if (!slot || !step) return;

    const baseLabel = step.querySelector("[data-base-selected-label]");
    if (baseLabel) {
      baseLabel.textContent = slot.baseColorName || "";
    }

    const addInLabel = step.querySelector("[data-add-in-selected-label]");
    if (addInLabel) {
      if (slot.addIn === "none") {
        const noneSwatch = step.querySelector(
          '[data-add-in-swatch][data-add-in-handle="none"]',
        );
        addInLabel.textContent = noneSwatch?.dataset.addInName || "";
      } else {
        addInLabel.textContent = slot.addInName || "";
      }
    }
  }

  clearColorSteps() {
    if (this.colorStepsContainer) {
      this.colorStepsContainer.innerHTML = "";
    }
    this.selections.colorSlots = [];
  }

  /**
   * Rebuild color steps from saved state when returning to customizer mode
   */
  rebuildColorSteps() {
    if (!this.selections.pourStyle) return;

    const { colorCount, colorLabels } = this.selections.pourStyle;
    const savedSlots = [...this.selections.colorSlots];

    // Regenerate the steps (this clears colorSlots)
    this.generateColorSteps(colorCount, colorLabels);

    // Restore the saved selections
    this.selections.colorSlots = savedSlots;

    // Restore price modifiers from saved slots
    this.priceModifiers.baseColors = savedSlots.reduce(
      (sum, slot) => sum + (slot.baseColorPrice || 0),
      0,
    );
    this.priceModifiers.addIns = savedSlots.reduce(
      (sum, slot) => sum + (slot.addInPrice || 0),
      0,
    );

    // Restore UI state for each step
    const steps =
      this.colorStepsContainer.querySelectorAll("[data-step-index]");
    steps.forEach((step) => {
      const slotIndex = parseInt(step.dataset.colorSlot);
      const slot = savedSlots[slotIndex];
      if (!slot) return;

      // Restore base color selection (note: attribute is data-color-handle)
      if (slot.baseColor) {
        const baseSwatch = step.querySelector(
          `[data-base-swatch][data-color-handle="${slot.baseColor}"]`,
        );
        if (baseSwatch) {
          baseSwatch.classList.add("is-selected");
          const input = baseSwatch.querySelector("[data-base-input]");
          if (input) input.checked = true;
        }
      }

      // Restore add-in selection
      if (slot.addIn && slot.addIn !== "none") {
        const addInSwatch = step.querySelector(
          `[data-add-in-swatch][data-add-in-handle="${slot.addIn}"]`,
        );
        if (addInSwatch) {
          addInSwatch.classList.add("is-selected");
          const input = addInSwatch.querySelector("[data-add-in-input]");
          if (input) input.checked = true;
        }
      } else if (slot.addIn === "none") {
        // Make sure "none" option is selected
        const noneSwatch = step.querySelector(
          `[data-add-in-swatch][data-add-in-handle="none"]`,
        );
        if (noneSwatch) {
          noneSwatch.classList.add("is-selected");
          const input = noneSwatch.querySelector("[data-add-in-input]");
          if (input) input.checked = true;
        }
      }

      this.updateStepSelectionLabels(step, slotIndex);
    });

    // Update progress and show first step
    this.totalSteps = 1 + colorCount;
    this.updateProgress();
    this.navigateToStep(this.currentStep);

    // Update preview with restored colors
    this.updatePreviewColors();
  }

  // ==================== NAVIGATION ====================

  navigateToStep(stepIndex) {
    // Clamp step index
    stepIndex = Math.max(0, Math.min(stepIndex, this.totalSteps - 1));

    this.currentStep = stepIndex;

    // Show/hide steps
    const allSteps = this.querySelectorAll("[data-step-index]");
    allSteps.forEach((step) => {
      const idx = parseInt(step.dataset.stepIndex);
      step.hidden = idx !== stepIndex;

      // Add animation class
      if (idx === stepIndex) {
        step.classList.add("is-active");

        // Update Next button state on last color step
        const nextButton = step.querySelector("[data-next-button]");
        if (nextButton) {
          const isLastStep = stepIndex === this.totalSteps - 1;
          nextButton.textContent = isLastStep ? "Complete" : "Next";
          // Style complete button differently - keep enabled for scroll-to-cart behavior
          nextButton.classList.toggle("button--complete", isLastStep);
        }
      } else {
        step.classList.remove("is-active");
      }
    });

    // Update progress
    this.updateProgress();
  }

  validateCurrentStep() {
    // Pour style step
    if (this.currentStep === 0) {
      return this.selections.pourStyle !== null;
    }

    // Color steps
    const slotIndex = this.currentStep - 1;
    const slot = this.selections.colorSlots[slotIndex];

    // Must have base color selected
    return slot && slot.baseColor !== null;
  }

  updateProgress() {
    if (!this.progressIndicator) return;

    // Only show progress in customizer mode with color steps
    if (this.mode !== "customizer" || this.totalSteps <= 1) {
      this.progressIndicator.hidden = true;
      return;
    }

    this.progressIndicator.hidden = false;

    const progress = ((this.currentStep + 1) / this.totalSteps) * 100;

    if (this.progressFill) {
      this.progressFill.style.setProperty("--progress", `${progress}%`);
    }

    if (this.progressText) {
      this.progressText.textContent = `Step ${this.currentStep + 1} of ${this.totalSteps}`;
    }
  }

  /**
   * Scroll to the buy buttons section when customization is complete
   */
  scrollToBuyButtons() {
    // Find buy-buttons element associated with this form
    const buyButtons = document.querySelector(
      `buy-buttons[form="${this.formId}"]`,
    );
    if (buyButtons) {
      buyButtons.scrollIntoView({ behavior: "smooth", block: "center" });
      // Focus the submit button after scroll completes
      setTimeout(() => {
        const submitBtn = buyButtons.querySelector('button[type="submit"]');
        if (submitBtn) {
          submitBtn.focus();
        }
      }, 500);
    }
  }

  // ==================== PREVIEW ====================

  updatePreview() {
    if (this.mode === "signature") {
      // Signature preview handled by handleSignatureSelect
    } else {
      this.updatePreviewPourStyle(this.selections.pourStyle?.handle);
      this.updatePreviewColors();
    }
  }

  async updatePreviewPourStyle(pourHandle) {
    if (!pourHandle) {
      console.warn("[ColorCustomizer] No pour handle provided");
      return;
    }
    if (!this.pourLayer) {
      console.warn("[ColorCustomizer] Pour layer element not found");
      return;
    }

    const svgUrl = this.selections.pourStyle?.svgUrl;
    console.log(
      "[ColorCustomizer] Updating pour style:",
      pourHandle,
      "URL:",
      svgUrl,
    );

    // If no SVG URL, use fallback solid color
    if (!svgUrl) {
      console.log("[ColorCustomizer] No SVG URL, using fallback");
      this.renderFallbackPourStyle();
      return;
    }

    try {
      // Fetch SVG (with caching)
      const svgContent = await this.fetchPourSvg(svgUrl);
      if (!svgContent) {
        console.warn("[ColorCustomizer] Empty SVG content received");
        this.renderFallbackPourStyle();
        return;
      }

      // Parse and inject the SVG
      this.injectPourSvg(svgContent, pourHandle);

      // Update colors now that SVG elements exist
      this.updatePreviewColors();
    } catch (error) {
      console.warn("[ColorCustomizer] Failed to load pour style SVG:", error);
      this.renderFallbackPourStyle();
    }
  }

  /**
   * Fetch pour style SVG with caching
   */
  async fetchPourSvg(url) {
    console.log("[ColorCustomizer] Fetching SVG from:", url);
    // Check cache first
    if (this.pourSvgCache.has(url)) {
      console.log("[ColorCustomizer] Using cached SVG");
      return this.pourSvgCache.get(url);
    }

    // Fix double /files/ if present
    const cleanUrl = url.replace("/files/files/", "/files/");
    console.log("[ColorCustomizer] Clean URL:", cleanUrl);

    const response = await fetch(cleanUrl);
    console.log("[ColorCustomizer] Fetch response status:", response.status);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const svgText = await response.text();
    console.log("[ColorCustomizer] Fetched SVG text, length:", svgText.length);
    this.pourSvgCache.set(url, svgText);
    return svgText;
  }

  /**
   * Parse SVG, namespace IDs, and inject into preview
   */
  injectPourSvg(svgText, pourHandle) {
    console.log(
      "[ColorCustomizer] Injecting SVG for:",
      pourHandle,
      "Text length:",
      svgText?.length,
    );
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");

    // Check for parse errors
    const parseError = doc.querySelector("parsererror");
    if (parseError) {
      console.warn(
        "[ColorCustomizer] SVG parse error:",
        parseError.textContent,
      );
      this.renderFallbackPourStyle();
      return;
    }

    const svg = doc.documentElement;
    const suffix = `-${this.sectionId}`;
    console.log(
      "[ColorCustomizer] SVG root element:",
      svg.tagName,
      "Section ID:",
      this.sectionId,
    );

    // Collect all elements with IDs and create a mapping
    const idMap = new Map();
    svg.querySelectorAll("[id]").forEach((el) => {
      const oldId = el.getAttribute("id");
      const newId = oldId + suffix;
      idMap.set(oldId, newId);
      el.setAttribute("id", newId);
    });

    // Update all url(#...) references to use namespaced IDs
    const updateUrlRefs = (el) => {
      // Check fill, stroke, mask, clip-path attributes
      ["fill", "stroke", "mask", "clip-path"].forEach((attr) => {
        const value = el.getAttribute(attr);
        if (value && value.startsWith("url(#")) {
          const match = value.match(/url\(#([^)]+)\)/);
          if (match && idMap.has(match[1])) {
            el.setAttribute(attr, `url(#${idMap.get(match[1])})`);
          }
        }
      });
      // Check style attribute for url() references
      const style = el.getAttribute("style");
      if (style && style.includes("url(#")) {
        let newStyle = style;
        idMap.forEach((newId, oldId) => {
          newStyle = newStyle.replace(
            new RegExp(`url\\(#${oldId}\\)`, "g"),
            `url(#${newId})`,
          );
        });
        el.setAttribute("style", newStyle);
      }
      // Recurse into children
      Array.from(el.children).forEach(updateUrlRefs);
    };
    updateUrlRefs(svg);

    // Extract defs and shapes
    const defs = svg.querySelector("defs");
    const shapes = Array.from(svg.children).filter(
      (child) => child.tagName.toLowerCase() !== "defs",
    );
    console.log(
      "[ColorCustomizer] Found",
      defs ? defs.children.length : 0,
      "defs,",
      shapes.length,
      "shapes",
    );

    // Clear previous content
    if (this.pourDefs) {
      this.pourDefs.innerHTML = "";
      console.log("[ColorCustomizer] Cleared pourDefs");
    } else {
      console.warn("[ColorCustomizer] pourDefs not found!");
    }
    if (this.addinDefs) this.addinDefs.innerHTML = "";

    // Clear pour layer (keep fallback but hide it)
    if (this.pourFallback) this.pourFallback.style.display = "none";

    if (!this.pourLayer) {
      console.warn("[ColorCustomizer] pourLayer not found!");
      return;
    }

    // Remove any previously injected content (not the fallback)
    Array.from(this.pourLayer.children).forEach((child) => {
      if (!child.hasAttribute("data-pour-fallback")) {
        child.remove();
      }
    });

    // Inject defs for pour layer
    if (defs && this.pourDefs) {
      Array.from(defs.children).forEach((def) => {
        this.pourDefs.appendChild(document.importNode(def, true));
      });
      console.log(
        "[ColorCustomizer] Injected",
        defs.children.length,
        "defs into pourDefs",
      );
    }

    // Inject shapes into pour layer
    shapes.forEach((shape) => {
      this.pourLayer.appendChild(document.importNode(shape, true));
    });
    console.log(
      "[ColorCustomizer] Injected",
      shapes.length,
      "shapes into pourLayer",
    );

    // Create add-in layer clone with swapped attributes
    this.createAddInLayerFromPour(svg, defs, shapes, idMap, suffix);

    // Store current pour handle
    this.pourLayer.dataset.currentPour = pourHandle;
  }

  /**
   * Create add-in layer by cloning pour content and swapping color attributes
   */
  createAddInLayerFromPour(svg, defs, shapes, idMap, suffix) {
    if (!this.addInLayer) return;

    // Clear previous add-in content
    if (this.addinFallback) this.addinFallback.style.display = "none";
    Array.from(this.addInLayer.children).forEach((child) => {
      if (!child.hasAttribute("data-addin-fallback")) {
        child.remove();
      }
    });

    // Clone and transform defs for add-in
    if (defs && this.addinDefs) {
      Array.from(defs.children).forEach((def) => {
        const clone = document.importNode(def, true);
        this.transformToAddIn(clone, suffix);
        this.addinDefs.appendChild(clone);
      });
    }

    // Clone and transform shapes for add-in
    shapes.forEach((shape) => {
      const clone = document.importNode(shape, true);
      this.transformToAddIn(clone, suffix);
      this.addInLayer.appendChild(clone);
    });
  }

  /**
   * Transform an element from pour colors to add-in colors
   * - Swap data-color-stop="N" to data-addin-stop="N"
   * - Swap --preview-color-N to --preview-add-in-N in styles
   * - Append "-addin" to IDs and url() references to avoid conflicts
   */
  transformToAddIn(el, suffix) {
    // Swap data attributes
    if (el.hasAttribute("data-color-stop")) {
      const stop = el.getAttribute("data-color-stop");
      el.removeAttribute("data-color-stop");
      el.setAttribute("data-addin-stop", stop);
    }

    // Transform style attribute
    const style = el.getAttribute("style");
    if (style) {
      let newStyle = style
        .replace(/--preview-color-(\d+)/g, "--preview-add-in-$1")
        .replace(/var\(--preview-color-(\d+)/g, "var(--preview-add-in-$1")
        // Also transform url() references in style (e.g., fill: url(#gradient))
        .replace(/url\(#([^)]+)\)/g, "url(#$1-addin)");
      el.setAttribute("style", newStyle);
    }

    // Transform ID (add -addin suffix)
    if (el.hasAttribute("id")) {
      const oldId = el.getAttribute("id");
      el.setAttribute("id", oldId + "-addin");
    }

    // Transform url() references in attributes
    ["fill", "stroke", "mask", "clip-path"].forEach((attr) => {
      const value = el.getAttribute(attr);
      if (value && value.startsWith("url(#")) {
        const match = value.match(/url\(#([^)]+)\)/);
        if (match) {
          el.setAttribute(attr, `url(#${match[1]}-addin)`);
        }
      }
    });

    // Recurse into children
    Array.from(el.children).forEach((child) => {
      this.transformToAddIn(child, suffix);
    });
  }

  /**
   * Render fallback when no SVG URL is available
   */
  renderFallbackPourStyle() {
    // Show fallback rects
    if (this.pourFallback) this.pourFallback.style.display = "";
    if (this.addinFallback) this.addinFallback.style.display = "";

    // Clear any injected content
    if (this.pourDefs) this.pourDefs.innerHTML = "";
    if (this.addinDefs) this.addinDefs.innerHTML = "";

    // Remove injected shapes from layers
    Array.from(this.pourLayer?.children || []).forEach((child) => {
      if (!child.hasAttribute("data-pour-fallback")) {
        child.remove();
      }
    });
    Array.from(this.addInLayer?.children || []).forEach((child) => {
      if (!child.hasAttribute("data-addin-fallback")) {
        child.remove();
      }
    });
  }

  updatePreviewColors() {
    if (!this.previewContainer) return;

    const slots = this.selections.colorSlots;
    const defaultColor = "#e0e0e0";

    // Set CSS custom properties and update SVG elements for each color slot
    // This supports N colors based on the pour style's color_selections_made count
    let hasAddIns = false;

    slots.forEach((slot, index) => {
      const stopNumber = index + 1; // 1-indexed for data attributes
      const baseColor = slot?.baseColorHex || defaultColor;
      const addInColor = slot?.addInHex || null;

      if (addInColor) hasAddIns = true;

      // Set CSS custom properties (for fallback styling)
      this.previewContainer.style.setProperty(
        `--preview-color-${stopNumber}`,
        baseColor,
      );
      this.previewContainer.style.setProperty(
        `--preview-add-in-${stopNumber}`,
        addInColor || "transparent",
      );

      // Update SVG elements with matching data-color-stop attribute
      // This handles both gradient stops (stop-color) and shape fills
      const colorStops = this.previewContainer.querySelectorAll(
        `[data-color-stop="${stopNumber}"]`,
      );
      colorStops.forEach((el) => {
        // Check if it's a gradient stop or a shape
        if (el.tagName.toLowerCase() === "stop") {
          el.style.stopColor = baseColor;
        } else {
          el.style.fill = baseColor;
        }
      });

      // Update add-in elements with matching data-addin-stop attribute
      // Use visibility to mask out shapes that don't have an add-in selected
      // For gradient stops, use transparent color since visibility doesn't work on <stop> elements
      const addInStops = this.previewContainer.querySelectorAll(
        `[data-addin-stop="${stopNumber}"]`,
      );
      addInStops.forEach((el) => {
        const isGradientStop = el.tagName.toLowerCase() === "stop";
        if (!addInColor) {
          // No add-in for this slot - hide it
          if (isGradientStop) {
            // Gradient stops don't support visibility - use transparent color
            el.style.stopColor = "transparent";
          } else {
            el.style.visibility = "hidden";
          }
        } else {
          if (isGradientStop) {
            el.style.stopColor = addInColor;
          } else {
            el.style.visibility = "visible";
            el.style.fill = addInColor;
          }
        }
      });
    });

    // Update add-in layer visibility
    if (this.addInLayer) {
      this.addInLayer.setAttribute("opacity", hasAddIns ? "0.7" : "0");
    }
  }

  /**
   * Load custom SVG mask from product metafield if available
   * The SVG URL is stored in data-custom-svg-url attribute on path elements
   */
  async loadCustomSvgMask() {
    // Find any element with custom SVG URL
    const elementWithUrl = this.querySelector("[data-custom-svg-url]");
    if (!elementWithUrl) return;

    let svgUrl = elementWithUrl.dataset.customSvgUrl;
    if (!svgUrl) return;

    // Fix double files/ path if present (e.g., /files/files/ -> /files/)
    svgUrl = svgUrl.replace("/files/files/", "/files/");

    try {
      // Show loading state
      const loadingEl = this.querySelector("[data-preview-loading]");
      if (loadingEl) loadingEl.hidden = false;

      // Fetch the SVG file
      const response = await fetch(svgUrl);
      if (!response.ok)
        throw new Error(`Failed to fetch SVG: ${response.status}`);

      const svgText = await response.text();

      // Parse the SVG to extract path data
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, "image/svg+xml");

      // Check for parse errors
      const parseError = svgDoc.querySelector("parsererror");
      if (parseError) {
        throw new Error("SVG parse error: " + parseError.textContent);
      }

      // Try to find the main path in the SVG
      // Look for path elements, preferring ones with significant d attribute
      const paths = svgDoc.querySelectorAll("path[d]");
      let bestPath = null;

      for (const path of paths) {
        const d = path.getAttribute("d");
        if (d && d.length > 10) {
          // Skip very short paths (likely decorative)
          if (!bestPath || d.length > bestPath.length) {
            bestPath = d;
          }
        }
      }

      if (bestPath) {
        // Update all path elements that need the custom mask
        const elements = this.querySelectorAll(
          "[data-product-mask-path], [data-product-clip-path], [data-product-outline], [data-pour-thumb-clip-path]",
        );
        elements.forEach((el) => {
          el.setAttribute("d", bestPath);
        });
      }

      // Hide loading state
      if (loadingEl) loadingEl.hidden = true;
    } catch (error) {
      console.warn("[ColorCustomizer] Failed to load custom SVG mask:", error);
      // Keep using default mask on error
      const loadingEl = this.querySelector("[data-preview-loading]");
      if (loadingEl) loadingEl.hidden = true;
    }
  }

  // ==================== LINE ITEM PROPERTY ====================

  updateLineItemProperty() {
    if (!this.lineItemContainer) return;

    // Clear existing inputs
    this.lineItemContainer.innerHTML = "";

    if (this.mode === "signature" && this.selections.signature) {
      // Single input for signature mode
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[Signature Coloration]`;
      input.value = this.selections.signature.name;
      input.setAttribute("form", this.formId);
      this.lineItemContainer.appendChild(input);
    } else if (this.mode === "customizer" && this.selections.pourStyle) {
      // Add pour style as first line item
      const pourInput = document.createElement("input");
      pourInput.type = "hidden";
      pourInput.name = `properties[Pour Style]`;
      pourInput.value = this.selections.pourStyle.name;
      pourInput.setAttribute("form", this.formId);
      this.lineItemContainer.appendChild(pourInput);

      // Create separate input for each color slot
      this.selections.colorSlots
        .filter((slot) => slot.baseColorName)
        .forEach((slot) => {
          const input = document.createElement("input");
          input.type = "hidden";
          // Use the slot label as the property name (e.g., "Fade from Tip", "Fade to Base")
          input.name = `properties[${slot.slotLabel}]`;
          // Value is the color selection (e.g., "Blue with Silver")
          let value = slot.baseColorName;
          if (slot.addInName) {
            value += ` with ${slot.addInName}`;
          }
          input.value = value;
          input.setAttribute("form", this.formId);
          this.lineItemContainer.appendChild(input);
        });
    }
  }

  // ==================== VARIANT SELECTION ====================

  updateVariantSelection() {
    if (!this.form) return;

    let optionName = null;
    let complexityHandle = null;
    let complexityName = null;
    let totalModifier = 0;

    if (this.mode === "signature" && this.selections.signature) {
      // For signature mode, use the pre-set complexity option name directly
      optionName = this.selections.signature.complexityOptionName;
      complexityHandle = this.selections.signature.complexityHandle;
      complexityName = this.selections.signature.name;
      totalModifier = this.selections.signature.priceModifier || 0;
    } else if (this.mode === "customizer") {
      // For customizer mode, calculate price and find matching complexity
      totalModifier = this.calculateTotalPriceModifier();
      const complexity = this.findMatchingComplexity(totalModifier);

      if (!complexity) return;

      optionName = complexity.optionName;
      complexityHandle = complexity.handle;
      complexityName = complexity.name;
    }

    if (!optionName) return;

    // Find the color complexity option input in the section
    // Inputs use HTML5 form attribute, so they're not inside the form element
    const complexityInput = this.findOptionInputByName(optionName);

    if (complexityInput && !complexityInput.checked) {
      // Check the input and dispatch change event
      complexityInput.checked = true;
      // Wrap in try-catch as the theme's variant picker may error during rapid updates
      try {
        complexityInput.dispatchEvent(new Event("change", { bubbles: true }));
      } catch (e) {
        // Theme's variant picker may fail during section re-renders - this is recoverable
        console.warn(
          "Variant selection dispatch error (recoverable):",
          e.message,
        );
      }
    }

    // Also dispatch custom event for any listeners
    this.dispatchEvent(
      new CustomEvent("color-customizer:complexity-change", {
        bubbles: true,
        detail: {
          complexityHandle,
          complexityName,
          optionName,
          priceModifier: totalModifier,
        },
      }),
    );
  }

  /**
   * Find an option input by its display name
   * This searches for labels containing the name text and returns the associated input
   */
  findOptionInputByName(optionName) {
    if (!optionName || !this.formId) return null;

    // Find the product section container - inputs are not inside the form element,
    // they use the HTML5 form attribute to associate with the form
    const section = this.closest(".shopify-section");
    if (!section) return null;

    // Find option inputs that are associated with our form (via form attribute)
    // or are inside the same section
    const optionInputs = section.querySelectorAll(
      `input[data-option-position][form="${this.formId}"], input[data-option-position]`,
    );

    for (const input of optionInputs) {
      // Get the label for this input (labels are siblings, not inside form)
      const label = section.querySelector(`label[for="${input.id}"]`);
      if (label) {
        // Check if the label text matches (look in sr-only span or direct text)
        const labelText =
          label.querySelector(".sr-only")?.textContent?.trim() ||
          label.textContent?.trim();
        if (labelText === optionName) {
          return input;
        }
      }
    }

    // Fallback: search by checking block-swatch labels that contain the text
    const allLabels = section.querySelectorAll("label[for]");
    for (const label of allLabels) {
      const labelText =
        label.querySelector(".sr-only")?.textContent?.trim() ||
        label.querySelector("span:last-child")?.textContent?.trim() ||
        label.textContent?.trim();

      if (labelText === optionName) {
        const inputId = label.getAttribute("for");
        const input = section.querySelector(`#${CSS.escape(inputId)}`);
        if (input && input.hasAttribute("data-option-position")) {
          return input;
        }
      }
    }

    return null;
  }

  calculateTotalPriceModifier() {
    let total = 0;

    if (this.mode === "signature" && this.selections.signature) {
      total = this.selections.signature.priceModifier || 0;
    } else if (this.mode === "customizer") {
      total =
        this.priceModifiers.pourStyle +
        this.priceModifiers.baseColors +
        this.priceModifiers.addIns;
    }

    return total;
  }

  findMatchingComplexity(targetPrice) {
    // Find complexity with matching or closest price
    // Sort by price and find closest match
    const sorted = [...this.colorComplexities].sort(
      (a, b) => a.price - b.price,
    );

    // Find exact match first
    const exact = sorted.find((c) => c.price === targetPrice);
    if (exact) return exact;

    // Find closest match that's >= target price
    const closest = sorted.find((c) => c.price >= targetPrice);
    return closest || sorted[sorted.length - 1];
  }

  // ==================== UTILITIES ====================

  formatMoney(cents) {
    // Simple money formatter - uses Shopify's format
    // In production, would use Shopify.formatMoney or theme's formatMoney
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }
}

// Register the custom element
customElements.define("color-customizer", ColorCustomizer);

/**
 * Keep custom line-item dropdown trigger labels in sync with selected radio values.
 * This powers the variant-picker-style dropdown UI for line item properties.
 */
document.addEventListener("change", (event) => {
  const input = event.target.closest("[data-line-item-dropdown-radio]");
  if (!input) return;

  const dropdown = input.closest("[data-line-item-dropdown]");
  if (!dropdown) return;

  const selectedValue = dropdown.querySelector(
    "[data-line-item-dropdown-selected-value]",
  );
  if (!selectedValue) return;

  selectedValue.textContent = input.value && input.value.trim() ? input.value : "--";
});